import lessRequire from '../../../config/less-require-resolver';
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import cn from 'classname';

import flow from 'lodash/fp/flow';
import upperFirst from 'lodash/upperFirst';

import Animate from '../../shared/animation';
import ShowMoreRows from '../../shared/table/show-more-rows';
import { notFound, mapTableSortHeaders } from '../../shared/table-helpers';
import { formatMoney } from '../../shared/format';
import { getCurrencyIcon } from '../../../../shared/js/get-currency-icon';
import {
	commentToCategoryMapper,
	categoryToNameMapper,
	pointCommentToCategoryMapper
} from '../index';

import PayoutBody from './payout-table/payout-body';
import CurrencyConversionBody from './currency-conversion-table/body';

lessRequire('modules/finances/components/table.less');

const FinancesTable = ({
	tableRows,
	tableHeaders,
	availableCurrencies,
	sortByColumn,
	columnSorting,
	showMore,
	isCanNotMoveToNext,
	tableTypeEnum,
	tableType,
	intl
}) => {
	const isHiddenRub = tableRows && tableRows.every(row => row.balance_rub === 0)
		&& process.env.CPA_ENV !== 'rocket';

	return (
		<Animate>
			<div className="finances-wrap__table">
				<table className="dashboard-table finances-table">
					<thead className="dashboard-table__head font-proxima-nova-semibold">
						<tr>
							{mapTableSortHeaders({
								headers: tableHeaders,
								columnSorting,
								isHiddenRub,
								sortByColumn,
								intl
							})
								.filter(header => !!header)
								.map((header, i) => (<th key={i}>{header}</th>))}
						</tr>
					</thead>
					{renderTableBody(tableTypeEnum, tableType, tableRows, availableCurrencies, intl, isHiddenRub)}
				</table>
				{!isCanNotMoveToNext && !!tableRows.length && showMore && (
					<ShowMoreRows showMore={() => showMore('FinancesTable')} />
				)}
			</div>
		</Animate>
	);
};

function withTableBody(body) {
	return (
		<tbody className="dashboard-table__body">{body}</tbody>
	);
}

function renderTableBody(tableTypeEnum, tableType, rows = [], availableCurrencies, intl, isHiddenRub) {
	switch (true) {
		case !rows.length :
			return withTableBody(notFound(intl));
		case tableTypeEnum.history === tableType:
			return withTableBody(historyRows(rows, intl));
		case tableTypeEnum.conversions === tableType:
			return (
				<CurrencyConversionBody
					className="dashboard-table__body"
					availableCurrencies={availableCurrencies}
					isHiddenRub={isHiddenRub}
					rows={rows}
				/>
			);
		case tableTypeEnum.payouts === tableType:
			return (
				<PayoutBody
					className="dashboard-table__body"
					rows={rows}
				/>
			);
		case tableTypeEnum.points === tableType:
			return withTableBody(pointsRows(rows, intl));
		default:
			return withTableBody(notFound(intl));
	}
}

const pointsRows = (rows, intl) => {

	return rows.map((row, i) => (
		<tr key={i}>
			<td className="history-date">
				<span>{moment(row.date).format('DD.MM.YY HH:mm:ss')}</span>
			</td>
			<td className='points-conversion'>{row.conversion_id || '--'}</td>
			<td className='points-amount history-balance'>
				<span className='amount-approved positive-amount'>{parseFloat(row.amount)}</span>
			</td>
			<td className='history-comments'>
				<span>{intl(pointCommentToCategoryMapper[row.status])}</span>
			</td>
		</tr>
	));
};

const historyRows = (rows, intl) => {
	return rows.map((row, i) => {
		const dateFormat = row.date.length > 10 ? 'DD.MM.YY HH:mm:ss' : 'DD.MM.YYYY';
		const categoryNameFromComment = flow(
			comment => ({
				category: commentToCategoryMapper(comment, Number(row.amount) < 0),
				comment
			}),
			({ category, comment}) => {
				const categoryName = intl(categoryToNameMapper[category]);
				let prepareComment = '';

				switch (category) {
					case 'income':
						if (comment === 'exchange goods to money') {
							prepareComment = `. ${upperFirst(intl(comment))}`;
						} else {
							prepareComment = `. ${upperFirst(comment.replace(/ Manager.*Period/, '. Period'))}`;
						}
						break;
					case 'approve':
					case 'withdrawal':
					case 'canceled':
						prepareComment = `. ${upperFirst(intl(comment))}`;
						break;
				}

				return `${categoryName}${prepareComment}`;
			});
		return (
			<tr key={i}>
				<td className="history-date">
					<span>{ moment(row.date).format(dateFormat) }</span>
				</td>
				<td className="history-hold">{ amount(row.hold_amount, row.currency) }</td>
				<td className="history-balance">{ amount(row.amount, row.currency) }</td>
				<td className="history-comments">
					<span>
					</span>
					<span>
						{categoryNameFromComment(row.comment)}
						{row.conversion_id && ` (${row.conversion_id})`}
						{row.bm_account_id && ` ${row.ba_name} (id ${row.bm_account_id})`}
					</span>
				</td>
			</tr>
		);
	});
};

const getSign = num => {
	if (Number(num) === 0) return '';
	return num > 0 ? '+' : '-';
};

const amount = (amount, currency) => (
	<span className={cn({
		'zero-amount': amount == '0.00',
		'positive-amount': amount > 0,
		'negative-amount': amount < 0
	})}
	>
		{getSign(amount)}{formatMoney(Math.abs(amount), true)} {getCurrencyIcon(currency)}
	</span>
);

FinancesTable.propTypes = {
	tableRows: PropTypes.array,
	tableHeaders: PropTypes.array,
	sortByColumn: PropTypes.func,
	columnSorting: PropTypes.object,
	showMore: PropTypes.func,
	isCanNotMoveToNext: PropTypes.bool,
	tableTypeEnum: PropTypes.object,
	tableType: PropTypes.string,
	intl: PropTypes.func
};

export default FinancesTable;



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/finances/components/table.js